import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  FlaskConical,
  SignalHigh,
  TrendingUp,
  TrendingDown,
  Briefcase,
  Activity,
  ArrowRight,
  Plus,
  WalletCards,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const fmtMoney = (v) =>
  v != null
    ? `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
    : '—';

const fmtPct = (v) =>
  v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—';

function StatCard({ label, value, sub, icon: Icon, tone = 'neutral' }) {
  const tones = {
    neutral: {
      icon: 'bg-primary/10 text-primary',
      value: 'text-foreground',
    },
    positive: {
      icon: 'bg-win/10 text-win',
      value: 'text-win',
    },
    negative: {
      icon: 'bg-loss/10 text-loss',
      value: 'text-loss',
    },
  };

  const t = tones[tone] || tones.neutral;

  return (
    <Card className="group overflow-hidden border-border/70 bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className={`mt-2 text-xl sm:text-2xl font-bold tracking-tight ${t.value}`}>
              {value}
            </p>
            {sub && (
              <p className="mt-1 text-[11px] sm:text-xs text-muted-foreground truncate">
                {sub}
              </p>
            )}
          </div>
          <div className={`rounded-xl p-2.5 shrink-0 ${t.icon}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="flex justify-between">
          <div>
            <Skeleton className="h-3 w-20 mb-3" />
            <Skeleton className="h-7 w-28 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-10 w-10 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

function PerformanceSnapshotSkeleton() {
  return (
    <Card className="overflow-hidden border-border/70 shadow-sm">
      <CardContent className="p-0">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
            <div>
              <Skeleton className="h-4 w-40 mb-2" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <Skeleton className="h-3 w-10 mb-2" />
              <Skeleton className="h-5 w-14" />
            </div>
            <div className="h-9 w-px bg-border" />
            <div>
              <Skeleton className="h-3 w-10 mb-2" />
              <Skeleton className="h-5 w-14" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BookRow({ book, onClick }) {
  const ret = book.cumulative_return_pct;
  const isUp = ret != null && ret >= 0;
  const isPaused = book.status === 'paused';
  const isStopped = book.status === 'stopped';
  const isActive = book.status === 'active';
  const noScreener = !book.active_screener_id;
  const isPaper = !!book.is_dry_run;

  return (
    <button
      onClick={onClick}
      className="group w-full border-b border-border/50 px-4 py-4 text-left transition-all last:border-0 hover:bg-muted/30 sm:px-5"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-center sm:gap-4">
        <div className="min-w-0 sm:col-span-5">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${
                isPaper
                  ? 'bg-blue-500/10 text-blue-600 ring-blue-500/15 dark:text-blue-400'
                  : 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/15 dark:text-emerald-400'
              }`}
            >
              {isPaper ? (
                <FlaskConical className="h-4 w-4" />
              ) : (
                <SignalHigh className="h-4 w-4" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-foreground">
                  {book.name}
                </span>
                {isActive && (
                  <span className="inline-flex rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Active
                  </span>
                )}
                {isPaused && (
                  <span className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                    Paused
                  </span>
                )}
                {isStopped && (
                  <span className="rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-rose-600 dark:text-rose-400">
                    Stopped
                  </span>
                )}
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                <span>{isPaper ? 'Paper' : 'Live'}</span>
                <span>·</span>
                <span>{book.open_positions ?? 0} open positions</span>
                {noScreener && (
                  <>
                    <span>·</span>
                    <span className="text-amber-600 dark:text-amber-400">
                      No screener
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:col-span-5 sm:grid-cols-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Value
            </p>
            <p className="mt-1 text-sm font-semibold text-foreground">
              {fmtMoney(book.portfolio_value)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Trades
            </p>
            <p className="mt-1 text-sm font-semibold text-foreground">
              {book.summary?.total_trades ?? 0}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Return
            </p>
            <p
              className={`mt-1 flex items-center gap-1 text-sm font-bold ${
                isUp ? 'text-win' : ret != null ? 'text-loss' : 'text-muted-foreground'
              }`}
            >
              {ret != null &&
                (isUp ? (
                  <TrendingUp className="h-3.5 w-3.5" />
                ) : (
                  <TrendingDown className="h-3.5 w-3.5" />
                ))}
              {fmtPct(ret)}
            </p>
          </div>
        </div>

        <div className="hidden justify-end sm:col-span-2 sm:flex">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-all group-hover:bg-primary/10 group-hover:text-primary">
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </button>
  );
}

function SectionSkeleton() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 border-b border-border/40 px-4 py-4 last:border-0">
          <Skeleton className="h-9 w-9 rounded-xl shrink-0" />
          <div className="flex-1">
            <Skeleton className="mb-2 h-4 w-36" />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="hidden h-4 w-16 sm:block" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="m-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 px-6 py-10 text-center sm:py-12">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
        <SignalHigh className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold text-foreground">No trading books yet</p>
      <p className="mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
        Create a live book to start monitoring performance, or a paper book to
        validate your strategy with zero real capital.
      </p>
      <button
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition hover:opacity-90"
      >
        <Plus className="h-3.5 w-3.5" />
        Create a Book
      </button>
    </div>
  );
}

const STATUS_ORDER = { active: 0, paused: 1, stopped: 2 };

export default function Dashboard() {
  const navigate = useNavigate();
  const [allLiveBooks, setAllLiveBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api
      .get('/live/books')
      .then((res) => setAllLiveBooks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const sortedBooks = useMemo(() => {
    return [...allLiveBooks].sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 3;
      const sb = STATUS_ORDER[b.status] ?? 3;
      if (sa !== sb) return sa - sb;
      return (b.portfolio_value ?? 0) - (a.portfolio_value ?? 0);
    });
  }, [allLiveBooks]);

  const paperBooks = useMemo(
    () => allLiveBooks.filter((b) => b.is_dry_run),
    [allLiveBooks]
  );
  const liveBooks = useMemo(
    () => allLiveBooks.filter((b) => !b.is_dry_run),
    [allLiveBooks]
  );

  const activeCount = allLiveBooks.filter((b) => b.status === 'active').length;
  const openPositions = allLiveBooks.reduce(
    (s, b) => s + (b.open_positions ?? 0),
    0
  );
  const totalPortfolio = allLiveBooks.reduce(
    (s, b) => s + (b.portfolio_value ?? 0),
    0
  );
  const totalLive = liveBooks.reduce(
    (s, b) => s + (b.portfolio_value ?? 0),
    0
  );

  const liveReturn = liveBooks.length
    ? liveBooks.reduce((s, b) => s + (b.cumulative_return_pct ?? 0), 0) /
      liveBooks.length
    : null;

  const paperReturn = paperBooks.length
    ? paperBooks.reduce((s, b) => s + (b.cumulative_return_pct ?? 0), 0) /
      paperBooks.length
    : null;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-5 md:px-6 md:py-7">
        {/* Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                Portfolio
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {getGreeting()}, {user?.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Here&apos;s how your trading books are performing.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <span className="text-xs font-medium text-muted-foreground">{today}</span>
            <button
              onClick={() => navigate('/live')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" />
              New Book
            </button>
          </div>
        </header>

        {/* KPI cards */}
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {loading ? (
            [1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              <StatCard
                label="Total Portfolio"
                value={fmtMoney(totalPortfolio)}
                sub={`${allLiveBooks.length} trading book${allLiveBooks.length !== 1 ? 's' : ''}`}
                icon={WalletCards}
              />
              <StatCard
                label="Live Portfolio"
                value={fmtMoney(totalLive)}
                sub={liveReturn != null ? `Avg return ${fmtPct(liveReturn)}` : 'No live books'}
                icon={SignalHigh}
                tone={liveReturn != null ? (liveReturn >= 0 ? 'positive' : 'negative') : 'neutral'}
              />
              <StatCard
                label="Open Positions"
                value={openPositions}
                sub="Across all books"
                icon={Briefcase}
              />
              <StatCard
                label="Active Books"
                value={activeCount}
                sub={`${allLiveBooks.length} total`}
                icon={Activity}
              />
            </>
          )}
        </section>

        {/* Performance snapshot */}
        {loading ? (
          <PerformanceSnapshotSkeleton />
        ) : (
          allLiveBooks.length > 0 && (
            <Card className="overflow-hidden border-border/70 shadow-sm">
              <CardContent className="p-0">
                <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <BarChart3 className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Performance snapshot</p>
                      <p className="text-xs text-muted-foreground">
                        Average cumulative return across your books
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Live
                      </p>
                      <p className={`mt-1 text-lg font-bold ${liveReturn != null && liveReturn >= 0 ? 'text-win' : liveReturn != null ? 'text-loss' : 'text-muted-foreground'}`}>
                        {fmtPct(liveReturn)}
                      </p>
                    </div>
                    <div className="h-9 w-px bg-border" />
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Paper
                      </p>
                      <p className={`mt-1 text-lg font-bold ${paperReturn != null && paperReturn >= 0 ? 'text-win' : paperReturn != null ? 'text-loss' : 'text-muted-foreground'}`}>
                        {fmtPct(paperReturn)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* Unified trading books */}
        <Card className="overflow-hidden border-border/70 shadow-sm">
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border/60 px-4 py-4 sm:px-5">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-foreground">Trading Books</h2>
                  {!loading && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                      {allLiveBooks.length}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Live and paper strategies
                </p>
              </div>

              <button
                onClick={() => navigate('/live')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary transition hover:opacity-80"
              >
                View all
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {!loading && allLiveBooks.length > 0 && (
              <div className="hidden grid-cols-12 gap-4 border-b border-border/50 bg-muted/20 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
                <div className="col-span-5">Book</div>
                <div className="col-span-5 grid grid-cols-3 gap-3">
                  <span>Value</span>
                  <span>Trades</span>
                  <span>Return</span>
                </div>
                <div className="col-span-2" />
              </div>
            )}

            {loading ? (
              <SectionSkeleton />
            ) : allLiveBooks.length === 0 ? (
              <div className="p-1">
                <EmptyState onCreate={() => navigate('/live')} />
              </div>
            ) : (
              <div>
                {sortedBooks.map((book) => (
                  <BookRow
                    key={book.id}
                    book={book}
                    onClick={() => navigate(`/live/${book.id}`)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
